import React, { useEffect, useState } from 'react';
import HeaderBar from '../components/HeaderBar';
import { agentService, AgentSettings, AgentSettingsUpdate } from '../services/agentService';

const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #e5e7eb',
  fontSize: 14,
  boxSizing: 'border-box',
};
const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  marginBottom: 24,
};
const rowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 };
const sectionTitleStyle: React.CSSProperties = { fontSize: 16, fontWeight: 700, color: '#1f2937', margin: '0 0 16px 0' };

const timeForInput = (v: string) => (v ? v.slice(0, 5) : '');
const timeFromInput = (v: string) => (v.length === 5 ? `${v}:00` : v);

const AgentConfigPage: React.FC = () => {
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await agentService.getSettings();
        if (!cancelled) setSettings(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Error al cargar la configuración');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = <K extends keyof AgentSettings>(key: K, value: AgentSettings[K]) => {
    setSettings(prev => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      const payload: AgentSettingsUpdate = {
        globally_enabled: settings.globally_enabled,
        scheduler_interval_minutes: settings.scheduler_interval_minutes,
        default_daily_proactive_limit: settings.default_daily_proactive_limit,
        default_weekly_proactive_limit: settings.default_weekly_proactive_limit,
        default_min_minutes_between_interactions: settings.default_min_minutes_between_interactions,
        default_quiet_hours_start: settings.default_quiet_hours_start,
        default_quiet_hours_end: settings.default_quiet_hours_end,
        default_channel_preference: settings.default_channel_preference,
        max_tool_iterations: settings.max_tool_iterations,
        max_input_tokens_estimate: settings.max_input_tokens_estimate,
      };
      const updated = await agentService.updateSettings(payload);
      setSettings(updated);
      setSavedAt(Date.now());
    } catch (e: any) {
      setError(e?.message || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 0 }}>
      <HeaderBar title="Configuración del Agente" />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px 40px' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Cargando configuración...</div>
        ) : error && !settings ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>{error}</div>
        ) : settings ? (
          <>
            <div style={cardStyle}>
              <h2 style={sectionTitleStyle}>Estado global</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fafafa' }}>
                <input
                  type="checkbox"
                  checked={settings.globally_enabled}
                  onChange={e => update('globally_enabled', e.target.checked)}
                />
                <span>
                  <div style={{ fontWeight: 600, color: '#1f2937' }}>Agente habilitado globalmente</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Kill switch maestro — cuando está deshabilitado, el agente está apagado para todos.</div>
                </span>
              </label>

              <div style={{ marginTop: 16 }}>
                <label style={labelStyle}>Intervalo del scheduler (minutos)</label>
                <input
                  type="number"
                  min={1}
                  style={inputStyle}
                  value={settings.scheduler_interval_minutes}
                  onChange={e => update('scheduler_interval_minutes', Number(e.target.value))}
                />
              </div>

              <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
                Último tick del scheduler: {settings.last_scheduler_tick_at ? new Date(settings.last_scheduler_tick_at).toLocaleString('es-ES') : '—'}
              </div>
            </div>

            <div style={cardStyle}>
              <h2 style={sectionTitleStyle}>Valores por defecto</h2>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Canal por defecto</label>
                  <select
                    style={inputStyle}
                    value={settings.default_channel_preference}
                    onChange={e => update('default_channel_preference', e.target.value as AgentSettings['default_channel_preference'])}
                  >
                    <option value="chat">Chat</option>
                    <option value="email">Email</option>
                    <option value="both">Ambos</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Min. minutos entre interacciones</label>
                  <input
                    type="number"
                    min={0}
                    style={inputStyle}
                    value={settings.default_min_minutes_between_interactions}
                    onChange={e => update('default_min_minutes_between_interactions', Number(e.target.value))}
                  />
                </div>
              </div>

              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Límite diario proactivo</label>
                  <input
                    type="number"
                    min={0}
                    style={inputStyle}
                    value={settings.default_daily_proactive_limit}
                    onChange={e => update('default_daily_proactive_limit', Number(e.target.value))}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Límite semanal proactivo</label>
                  <input
                    type="number"
                    min={0}
                    style={inputStyle}
                    value={settings.default_weekly_proactive_limit}
                    onChange={e => update('default_weekly_proactive_limit', Number(e.target.value))}
                  />
                </div>
              </div>

              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Inicio horas silencio</label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={timeForInput(settings.default_quiet_hours_start)}
                    onChange={e => update('default_quiet_hours_start', timeFromInput(e.target.value))}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Fin horas silencio</label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={timeForInput(settings.default_quiet_hours_end)}
                    onChange={e => update('default_quiet_hours_end', timeFromInput(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div style={cardStyle}>
              <h2 style={sectionTitleStyle}>Límites de ejecución</h2>
              <div style={rowStyle}>
                <div>
                  <label style={labelStyle}>Max. iteraciones de tools por run</label>
                  <input
                    type="number"
                    min={1}
                    style={inputStyle}
                    value={settings.max_tool_iterations}
                    onChange={e => update('max_tool_iterations', Number(e.target.value))}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Max. tokens de entrada estimados</label>
                  <input
                    type="number"
                    min={0}
                    style={inputStyle}
                    value={settings.max_input_tokens_estimate}
                    onChange={e => update('max_input_tokens_estimate', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {error && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: saving ? '#9ca3af' : '#38b2ac',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 20px',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
              {savedAt && !saving && (
                <span style={{ fontSize: 13, color: '#10b981' }}>Guardado ✓</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
                Actualizado: {new Date(settings.updated_at).toLocaleString('es-ES')}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AgentConfigPage;
